import { useCallback, useEffect, useRef, useState } from "react";
import { type ConsentData, useAuth } from "@shared/contexts/auth-context";
import { toast } from "@shared/helper_lib/hooks/use-toast";

interface UseYandexAuthReturn {
  isLoading: boolean;
  handleAuth: () => void;
}

export interface YandexAuthResult {
  success: boolean;
  message?: string;
  isNewUser?: boolean;
}

// Ключи обмена между вкладкой сайта и окном авторизации.
// postMessage использовать нельзя: Яндекс возвращает окно на бекенд, который отдаёт
// Cross-Origin-Opener-Policy: same-origin, и браузер разрывает связь с window.opener
export const YANDEX_POPUP_FLAG = "yandex_auth_popup";
export const YANDEX_RESULT_KEY = "yandex_auth_result";
export const YANDEX_CONSENTS_KEY = "yandex_auth_consents";
export const YANDEX_POPUP_TTL = 10 * 60 * 1000;

const POPUP_FEATURES = "width=600,height=700,menubar=no,toolbar=no,location=no,status=no";

export function useYandexAuth(consents?: ConsentData): UseYandexAuthReturn {
  const [isLoading, setIsLoading] = useState(false);
  const { refreshUser, openAuthModal } = useAuth();

  const popupRef = useRef<Window | null>(null);
  const storageHandlerRef = useRef<((event: StorageEvent) => void) | null>(null);
  const closeWatcherRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopCloseWatcher = useCallback((): void => {
    if (closeWatcherRef.current) {
      clearInterval(closeWatcherRef.current);
      closeWatcherRef.current = null;
    }
  }, []);

  const cleanup = useCallback((): void => {
    stopCloseWatcher();
    if (storageHandlerRef.current) {
      window.removeEventListener("storage", storageHandlerRef.current);
      storageHandlerRef.current = null;
    }
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }
    popupRef.current = null;
  }, [stopCloseWatcher]);

  useEffect(() => cleanup, [cleanup]);

  const buildAuthUrl = (): string => {
    const clientId = import.meta.env.VITE_YANDEX_CLIENT_ID;
    const redirectUri = import.meta.env.VITE_YANDEX_REDIRECT_URI || `${window.location.origin}/auth/yandex/callback`;

    const authUrl = new URL("https://oauth.yandex.ru/authorize");
    authUrl.searchParams.set("response_type", "token");
    authUrl.searchParams.set("client_id", clientId);
    authUrl.searchParams.set("redirect_uri", redirectUri);

    return authUrl.toString();
  };

  const handleResult = useCallback(
    async (result: YandexAuthResult): Promise<void> => {
      if (result.success) {
        // Cookie уже установлена в окне авторизации - достаточно перечитать профиль
        await refreshUser();
        return;
      }

      if (result.isNewUser) {
        openAuthModal("signup");
        toast({
          title: "Аккаунт не найден",
          description: "Пожалуйста, зарегистрируйтесь, приняв условия оферты и политику конфиденциальности",
          variant: "destructive"
        });
        return;
      }

      toast({
        title: "Ошибка авторизации",
        description: result.message || "Ошибка при авторизации через Яндекс",
        variant: "destructive"
      });
    },
    [openAuthModal, refreshUser]
  );

  const handleAuth = useCallback(() => {
    setIsLoading(true);
    cleanup();

    // Согласия передаём через localStorage: вход завершает окно авторизации, а не эта вкладка
    if (consents) {
      localStorage.setItem(YANDEX_CONSENTS_KEY, JSON.stringify(consents));
    } else {
      localStorage.removeItem(YANDEX_CONSENTS_KEY);
    }
    localStorage.removeItem(YANDEX_RESULT_KEY);

    const authUrl = buildAuthUrl();
    localStorage.setItem(YANDEX_POPUP_FLAG, String(Date.now()));
    const popup = window.open(authUrl, "yandex-auth", POPUP_FEATURES);

    // Попап заблокирован - откатываемся на редирект всей страницы
    if (!popup) {
      localStorage.removeItem(YANDEX_POPUP_FLAG);
      window.location.href = authUrl;
      return;
    }

    popupRef.current = popup;

    const storageHandler = async (event: StorageEvent): Promise<void> => {
      if (event.key !== YANDEX_RESULT_KEY || !event.newValue) {
        return;
      }

      let result: YandexAuthResult;
      try {
        result = JSON.parse(event.newValue);
      } catch {
        return;
      }

      localStorage.removeItem(YANDEX_RESULT_KEY);
      cleanup();
      setIsLoading(false);

      await handleResult(result);
    };

    storageHandlerRef.current = storageHandler as (event: StorageEvent) => void;
    window.addEventListener("storage", storageHandlerRef.current);

    // Окно могли закрыть вручную - снимаем индикатор загрузки, но слушатель оставляем:
    // из-за COOP ссылка на окно может считаться закрытой ещё до реального завершения входа
    closeWatcherRef.current = setInterval(() => {
      if (popupRef.current?.closed) {
        stopCloseWatcher();
        setIsLoading(false);
      }
    }, 500);
  }, [cleanup, consents, handleResult, stopCloseWatcher]);

  return {
    isLoading,
    handleAuth
  };
}
